import { apiRequest } from '@/utils/api-request';
import type {
  ExpressionAst,
  JsonValue,
} from '@/pages/workflow/helper/functional-control-flow';

/**
 * `POST /api/v1/orgs/{org}/expressions/preview`: the backend parses formula
 * text into the expression tree and, given a sample, evaluates it. It is the
 * only formula parser, so what the editor shows is what a run would do.
 */

export interface FormulaPreviewItem {
  name?: string | null;
  formula?: string | null;
  expression?: ExpressionAst | null;
}

export interface ExpressionPreviewRequest {
  formulas: FormulaPreviewItem[];
  sample?: JsonValue;
  context?: JsonValue;
  /** Context key bare names belong to: `value` (Switch/Validate/Calculate), `judge` (loop exit). */
  root?: string;
  /** `calculate`: outputs evaluated in order, earlier names usable later; `condition`: each alone. */
  mode?: 'calculate' | 'condition';
  extra_roots?: Record<string, JsonValue>;
}

export interface PreviewProblem {
  code: string;
  message: string;
  hint?: string | null;
  field?: string | null;
  position?: number | null;
}

export interface FormulaPreviewResult {
  name?: string | null;
  formula?: string | null;
  expression?: ExpressionAst | null;
  fields: string[];
  value: JsonValue;
  evaluated: boolean;
  is_boolean?: boolean | null;
  error?: PreviewProblem | null;
}

export interface ExpressionPreviewResponse {
  ok: boolean;
  results: FormulaPreviewResult[];
  grammar: {
    operators: string[];
    functions: string[];
    comprehensions: string[];
    limits: Record<string, number>;
  };
}

export async function previewExpressions(
  orgId: string,
  request: ExpressionPreviewRequest,
): Promise<ExpressionPreviewResponse> {
  const response = await apiRequest<ExpressionPreviewResponse>({
    url: `/api/v1/orgs/${encodeURIComponent(orgId)}/expressions/preview`,
    method: 'POST',
    target: 'workflow',
    body: { ...request } as Record<string, unknown>,
  });
  if (response.detail || response.success === false || !response.data) {
    throw new Error(response.detail || 'The formula could not be checked.');
  }
  return response.data;
}
